/* eslint-disable no-unused-vars */
import {useEffect, useRef, useState} from "react";
import clsx from "clsx";
import * as Yup from "yup";
import {useHistory} from "react-router-dom";
import {DashboardLayout} from "components/Layout";
import {FieldArray, Formik} from "formik";
import {SubmitButton} from "components/Button";
import {useToast} from "hooks/useToast";
import {convertErrorMessageFormat} from "utils/converter";
import {useEditMedicine, useDetailMedicine} from "hooks/SuperAdmin/Medicine";
import {CircleLoader} from "components/Loader";
import {FormCard} from "components/Form";
import {useParams} from "react-router-dom";
import {BasicDropdown} from "components/Dropdown";
import {useMedicineCategoryOptions} from "hooks/SuperAdmin/MedicineCategory";
import {useMedicineUnitOptions} from "hooks/SuperAdmin/MedicineUnit";
import {useSediaanOptions} from "hooks/SuperAdmin/SediaanObat";
import {Col, Row} from "react-bootstrap";

const FormSchemaValidation = Yup.object().shape({
	name: Yup.string().required("Nama obat wajib di isi"),
	medicineCategoryId: Yup.string().required("Kategori obat wajib di isi"),
	obat: Yup.array().of(
		Yup.object().shape({
			sediaan: Yup.number().required("Sediaan obat wajib di isi"),
			unit: Yup.number().required("Unit obat wajib di isi"),
		}),
	),
});
const FormInitialValues = {name: "", medicineCategoryId: "", obat: []};

export function MedicineEditPage() {
	const {showToast} = useToast();
	const formikRef = useRef();
	const history = useHistory();
	const params = useParams();
	const {id} = params;
	const {isLoading: loadingFetcing, error, isError, data: detailMedicineUnit} = useDetailMedicine(id);
	const {mutate, isLoading: isSubmitting} = useEditMedicine();
	const [medicineCategoryOptions, setMedicineCategoryOptions] = useState([]);
	const {data: medicineCategories} = useMedicineCategoryOptions();
	const [medicineUnitOptions, setMedicineUnitOptions] = useState([]);
	const {data: medicineUnits} = useMedicineUnitOptions();
	const [sediaanOptions, setSediaanOptions] = useState([]);
	const {data: sediaanList} = useSediaanOptions();

	const handleSubmit = formData => {
		const payload = {
			medicineCategoryId: formData.medicineCategoryId,
			name: formData.name,
			units: formData.obat.map(o => o.unit),
			sediaans: formData.obat.map(o => o.sediaan),
		};

		mutate(
			{id, ...payload},
			{
				onSuccess: () => {
					showToast("success", "Data berhasil diperbarui");
					history.push("/obat");
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};

	useEffect(() => {
		if (medicineCategories?.success) {
			const dataMedicineCategory = medicineCategories?.data?.rows?.map(medicineCategory => ({
				value: medicineCategory?.id + "",
				label: `${medicineCategory?.name}`,
			}));

			setMedicineCategoryOptions([...dataMedicineCategory]);
			formikRef?.current?.setFieldValue(`medicineCategoryId`, dataMedicineCategory[0].value);
		}
	}, [medicineCategories]);

	useEffect(() => {
		if (medicineUnits?.success) {
			const dataMedicineUnit = medicineUnits?.data?.rows?.map(medicineUnit => ({
				value: medicineUnit?.id,
				label: `${medicineUnit?.name}`,
			}));

			setMedicineUnitOptions([...dataMedicineUnit]);
			formikRef?.current?.setFieldValue(`obat.0.unit`, dataMedicineUnit[0].value);
		}
	}, [medicineUnits]);

	useEffect(() => {
		if (sediaanList?.success) {
			const dataSediaan = sediaanList?.data?.rows?.map(sd => ({
				value: sd?.id,
				label: `${sd?.name}`,
			}));

			setSediaanOptions([...dataSediaan]);
			formikRef?.current?.setFieldValue(`obat.0.sediaan`, dataSediaan[0].value);
		}
	}, [sediaanList]);

	useEffect(() => {
		if (detailMedicineUnit) {
			formikRef?.current?.setFieldValue("name", detailMedicineUnit?.data?.name);
			formikRef?.current?.setFieldValue("medicineCategoryId", detailMedicineUnit?.data?.medicineCategoryId);

			for (let i = 0; i < detailMedicineUnit?.data?.unitV2?.length; i++) {
				const unit = detailMedicineUnit?.data?.unitV2?.[i];

				formikRef?.current?.setFieldValue(`obat.${i}.unit`, unit.unitId);
			}

			for (let i = 0; i < detailMedicineUnit?.data?.sediaan?.length; i++) {
				const sediaan = detailMedicineUnit?.data?.sediaan?.[i];

				formikRef?.current?.setFieldValue(`obat.${i}.sediaan`, sediaan.sediaanId);
			}
		}
	}, [detailMedicineUnit]);

	if (loadingFetcing) {
		return (
			<DashboardLayout>
				<FormCard title="Edit Obat">
					<CircleLoader />
				</FormCard>
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<Formik
				initialValues={FormInitialValues}
				innerRef={formikRef}
				validationSchema={FormSchemaValidation}
				onSubmit={handleSubmit}>
				{({handleSubmit, handleBlur, handleChange, values, errors, touched, setFieldValue}) => (
					<FormCard
						backTitle="Daftar List Obat"
						title="Edit Obat"
						onBackButtonClick={() => history.push("/obat")}>
						<div class="row gx-2 mb-5">
							<div class="col-sm-6 col-6">
								<div class="setting-item">
									<label class="setting-label required">Kategori Obat</label>
									<BasicDropdown
										className={clsx(
											"form-control",
											errors?.medicineCategoryId &&
												touched?.medicineCategoryId &&
												"form-control-error",
										)}
										data={medicineCategoryOptions}
										name="medicineCategoryId"
										value={values?.medicineCategoryId}
										onSelect={fieldValue => {
											setFieldValue("medicineCategoryId", fieldValue);
										}}
									/>
									{errors.medicineCategoryId && touched.medicineCategoryId && (
										<p className="form-error-item-message">{errors.medicineCategoryId}</p>
									)}
								</div>
							</div>
						</div>

						<div class="row gx-2 mb-5">
							<div class="col-sm-6 col-6">
								<div class="setting-item">
									<label class="setting-label required">Nama Obat</label>
									<input
										class={clsx(
											"form-control",
											errors?.name && touched.name && "form-control-error",
										)}
										disabled={loadingFetcing}
										name="name"
										placeholder=""
										type="text"
										value={values?.name}
										onBlur={handleBlur}
										onChange={handleChange}
									/>
									{errors.name && touched.name && (
										<p className="form-error-item-message">{errors.name}</p>
									)}
								</div>
							</div>
						</div>

						<FieldArray name="obat">
							{({remove, push}) => (
								<>
									{values.obat?.map((o, idx) => {
										return (
											<Row key={`unit-${idx}`} className="gx-2 mb-5">
												<Col>
													<div class="setting-item">
														{idx === 0 ? (
															<label class="setting-label required">Unit Obat</label>
														) : null}
														<BasicDropdown
															className={clsx(
																"form-control",
																errors?.obat?.[idx] && "form-control-error",
															)}
															data={medicineUnitOptions}
															name={`obat.${idx}.unit`}
															value={o.unit}
															onSelect={fieldValue => {
																setFieldValue(`obat.${idx}.unit`, Number(fieldValue));
															}}
														/>
														{errors.obat?.[idx] && touched.obat?.[idx] && (
															<p className="form-error-item-message">
																{errors.obat?.[idx]}
															</p>
														)}
													</div>
												</Col>
												<Col>
													<div class="setting-item">
														{idx === 0 ? (
															<label class="setting-label required">Sediaan</label>
														) : null}
														<BasicDropdown
															className={clsx(
																"form-control",
																errors?.obat?.[idx] && "form-control-error",
															)}
															data={sediaanOptions}
															name={`obat.${idx}.sediaan`}
															value={o.sediaan}
															onSelect={fieldValue => {
																setFieldValue(
																	`obat.${idx}.sediaan`,
																	Number(fieldValue),
																);
															}}
														/>
														{errors.obat?.[idx] && touched.obat?.[idx] && (
															<p className="form-error-item-message">
																{errors.obat?.[idx]}
															</p>
														)}
													</div>
												</Col>
												<Col className="d-flex align-items-end" xs={1}>
													<div className="d-flex gap-2 mb-4">
														{idx === values?.obat.length - 1 ? (
															<a
																class={clsx("text-orange wait-pay")}
																style={{cursor: "pointer"}}
																onClick={() => {
																	push({
																		unit: medicineUnitOptions[0].value,
																		sediaan: sediaanOptions[0].value,
																	});
																}}>
																<span class="icon-ico-plus ft-18" />
															</a>
														) : null}
														{values?.obat.length > 1 ? (
															<a
																class={clsx("text-orange wait-pay")}
																style={{cursor: "pointer"}}
																onClick={() => {
																	remove(idx);
																}}>
																<span class="icon-ico-delete ft-18" />
															</a>
														) : null}
													</div>
												</Col>
											</Row>
										);
									})}
								</>
							)}
						</FieldArray>

						<div class="text-center my-3">
							<SubmitButton
								className="btn btn-orange d-inline-block mw-250"
								disabled={!FormSchemaValidation.isValidSync(values)}
								loading={isSubmitting}
								text="Simpan Perubahan"
								onClick={handleSubmit}
							/>
						</div>
					</FormCard>
				)}
			</Formik>
		</DashboardLayout>
	);
}
